'use client';

import { cn } from '@module/lib/utils';
import React, { Suspense } from 'react';
import { iconLoaders } from './components/loaders';
import { eagerIconComponents } from './eager';

// NOTE:
// - 이 컴포넌트는 아이콘을 반드시 클라이언트에서만 렌더링합니다.
// - React.lazy + Suspense(null) 패턴으로 SSR 시 마크업을 출력하지 않아
//   초기 HTML 사이즈에 영향을 주지 않습니다.

export type IconLoader = () => Promise<{
  default: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}>;
export type IconName = keyof typeof import('./components/loaders').iconLoaders;
export interface IconProps extends Omit<React.SVGProps<SVGSVGElement>, 'name'> {
  name: IconName;
  size?: number;
  strokeWidth?: number;
  title?: string;
  className?: string;
  /**
   * eager=true인 경우:
   * - 첫 렌더 직후 아이콘 모듈을 즉시 프리로드하여 체감 깜빡임을 줄입니다.
   * - Suspense fallback은 고정 크기 placeholder로 대체되어 레이아웃 시프트를 방지합니다.
   * 기본값은 false로, 기존 lazy 동작을 유지합니다.
   */
  eager?: boolean;
}
const FallbackIcon: React.FC<React.SVGProps<SVGSVGElement>> = () => null;
const FALLBACK_LOADER: IconLoader = async () => ({
  default: FallbackIcon
});
export default function Icon({
  name,
  size = 24,
  strokeWidth,
  title,
  className,
  color,
  eager = false,
  ...rest
}: IconProps) {
  const loader = (iconLoaders as Record<string, IconLoader>)[name as string] as IconLoader | undefined;

  // Memoize the lazy component by name to avoid remounts on each parent re-render
  const LazyIcon = React.useMemo(() => React.lazy(loader ?? FALLBACK_LOADER), [name]);
  // eager인 경우, 첫 렌더 직후 아이콘 모듈을 워밍업
  React.useEffect(() => {
    if (eager && loader) {
      void loader();
    }
  }, [eager, name]);
  if (!loader && process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(`Icon "${String(name)}" not found. Did you run pnpm --filter pandatv-module icons?`);
  }
  const isSemantic = Boolean(title);
  const a11y = isSemantic ? {
    role: 'img' as const,
    'aria-label': title
  } : {
    'aria-hidden': true
  };

  // eager인 경우, 첫 렌더 직후 아이콘 모듈을 워밍업
  React.useEffect(() => {
    if (eager && loader) {
      void loader();
    }
  }, [eager, name]);

  // eager + 화이트리스트 아이콘이면, 정적 import 컴포넌트로 즉시 렌더
  if (eager) {
    const EagerIcon = (eagerIconComponents as Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>>)[name as string];
    if (EagerIcon) {
      return <EagerIcon {...a11y} {...rest} className={cn('box-content', className)} strokeWidth={strokeWidth} width={size} height={size} color={color} />;
    }
  }

  // eager인 경우 레이아웃 시프트를 막기 위한 고정 크기 placeholder 제공
  const fallback = eager ? <svg width={size} height={size} className={className} aria-hidden focusable='false' style={{
    display: 'inline-block',
    visibility: 'hidden'
  }} /> : null;
  return <Suspense fallback={fallback}>
      <LazyIcon {...a11y} {...rest} className={className} strokeWidth={strokeWidth} width={size} height={size} color={color} />
    </Suspense>;
}

// Preload helpers to eliminate first-render flicker on icon swaps
export function preloadIcon(name: IconName) {
  const loader = (iconLoaders as Record<string, IconLoader>)[name as string];
  if (loader) {
    // Fire and forget to warm the dynamic import chunk
    void loader();
  }
}
export function preloadIcons(names: IconName[]) {
  names.forEach(preloadIcon);
}